import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import * as _ from 'lodash';
import {AgentModel} from "../../../../agents/angular/models/agent.model";
import {AgentsHttpService} from "../../../../agents/angular/services/agents-http.service";

export interface RouteAssistantAssignmentState {
  assistants: AgentModel[];
  cursor?: string;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class RouteAssistantAssignmentStore extends ComponentStore<RouteAssistantAssignmentState> {

  readonly assistants$ = this.select((state) => state.assistants);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  vm$ = this.select({
    assistants: this.assistants$,
    loading: this.loading$,
    error: this.error$,
    cursor: this.cursor$
  });

  constructor(
    @Inject(AgentsHttpService) private agentsHttpService: AgentsHttpService
  ) {
    super({
      assistants: [],
      cursor: '',
      loading: false,
      error: null,
    });
  }

  readonly onSetAssistants = this.updater((state: RouteAssistantAssignmentState, value: {
    assistants: AgentModel[],
    cursor?: string
  }) => ({
    ...state,
    assistants: _.uniqBy([...state.assistants, ...value.assistants], 'id'),
    cursor: value.cursor,
    loading: false,
    error: null,
  }));

  readonly onResetAssistants = this.updater((state: RouteAssistantAssignmentState) => ({
    ...state,
    assistants: [],
    cursor: undefined,
    loading: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  readonly getAssistants = this.effect(
    (trigger$: Observable<{limit: number, query?: string}>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)),
        withLatestFrom(this.cursor$),
        switchMap(([params, cursor]) => {
            return this.agentsHttpService.getAssistants(params.limit, params.query, cursor || undefined).pipe(
              tap({
                next: (response: {list: AgentModel[], cursor?: string}) => {
                  return this.onSetAssistants({assistants: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err),
                finalize: () => this.setLoading(false)
              })
            )
          }
        )
      )
  );

  readonly resetAssistants = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.onResetAssistants();
      })
    )
  );
}
